// SPDX-License-Identifier: Elastic-2.0
/**
 * CountBadge — small numeric pill (peach background, cream text).
 *
 * Hidden when `value` is undefined, 0, or negative. Caps at "99+" for
 * values > 99 (single-character overflow guard — common pattern in
 * Slack / Linear / iOS).
 *
 * Unlike TierBadge, the count is *information* the screen-reader user
 * needs — the badge carries its own `aria-label` and is NOT marked
 * `aria-hidden`. The sibling sidebar link is expected to compose its
 * own `aria-label` that includes the count (see functional spec § 7).
 *
 * Spec refs:
 * - specs/wip/feat-37-sidebar-ia-refresh/technical/sidebar-ia-refresh.md § 3
 * - specs/wip/feat-37-sidebar-ia-refresh/functional/sidebar-ia-refresh.md § 4
 */
import styles from "./CountBadge.module.css";

export interface CountBadgeProps {
  /** Current count. Renders null when <= 0 or undefined. */
  value: number | undefined;
  /** ARIA label for screen readers, e.g. "3 pending suggestions". */
  ariaLabel: string;
  className?: string;
}

export function CountBadge({
  value,
  ariaLabel,
  className,
}: CountBadgeProps): JSX.Element | null {
  if (value === undefined || value <= 0) return null;
  const display = value > 99 ? "99+" : String(value);
  const composed = [styles.badge, className].filter(Boolean).join(" ");
  return (
    <span className={composed} data-testid="count-badge" aria-label={ariaLabel}>
      {display}
    </span>
  );
}
